// Minimal consent layer. Replace with a full CMP later if needed.
// Categories: 'necessary' (implicit), 'analytics', 'ads'. Extend as needed.

export type ConsentState = {
  analytics: boolean;
  ads: boolean;
};

export const CONSENT_STORAGE_KEY = 'consent.v1';
export const defaultConsentState: ConsentState = {
  analytics: true,
  ads: true,
};

let state: ConsentState | null = null;

function readStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.ads === 'boolean'
    ) {
      return { analytics: parsed.analytics, ads: parsed.ads };
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredConsent(next: ConsentState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export function hasConsent(key: keyof ConsentState | 'necessary'): boolean {
  if (key === 'necessary') return true;
  return state?.[key] ?? defaultConsentState[key];
}

// Expose a minimal event based API future UI can hook into.
export function setConsent(next: Partial<ConsentState>) {
  state = { ...(state ?? defaultConsentState), ...next };
  writeStoredConsent(state);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('consentChanged', { detail: state }));
  }
}

export function getConsent(): ConsentState | null {
  return state;
}

export function onConsentChange(
  handler: (s: ConsentState) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => {
    const detail = (e as CustomEvent).detail as ConsentState | undefined;
    if (detail) handler(detail);
  };
  window.addEventListener('consentChanged', listener as EventListener);
  return () =>
    window.removeEventListener('consentChanged', listener as EventListener);
}

// Allow a future consent manager opener (button already emits openConsentManager)
if (typeof window !== 'undefined') {
  // hydrate from storage once on client
  const stored = readStoredConsent();
  if (stored) {
    state = stored;
  }

  window.addEventListener('openConsentManager', () => {
    // A separate manager could open here.
    // The banner component can also listen to this and re-open.
  });
}
